/**
 * computeSeedTo - the To line a fresh agent reply should start with.
 *
 * An agent's deliberate To-override (the "respond to a colleague" /
 * "requester is unreachable" case from commit 5c415ff) carries forward
 * from the most recent SENT public reply. It is NEVER taken from an
 * inbound customer email: a customer's To recipients are "who else they
 * happened to address", which we capture for DISPLAY only - not a routing
 * decision for our reply. Seeding the reply To from an inbound To line was
 * dropping the ticket owner off the recipients (the agent then could not
 * see who the reply was going to, and a reply could route away from the
 * requester). See plans/2026-06-16-lh3-recipient-requester-bug.md (Bug A).
 *
 * When there is no prior sent reply carrying an override, the reply
 * defaults to the requester. Returns lowercased addresses, deduped.
 */
import type { CommentResponse } from "@/types/api";

export function computeSeedTo(
  comments: readonly CommentResponse[],
  requesterEmail: string,
): string[] {
  const requester = requesterEmail.toLowerCase();
  // Most recent outbound agent reply. sent_via_email is the signal that
  // this comment was actually emailed by us (an agent reply / sent draft),
  // as opposed to an inbound customer message (sent_via_email === false).
  const lastSentReply = [...comments]
    .filter((c) => !c.is_private && c.sent_via_email)
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0];
  const overrideTo = (lastSentReply?.to_emails ?? []).map((e) =>
    e.toLowerCase(),
  );
  return overrideTo.length > 0 ? Array.from(new Set(overrideTo)) : [requester];
}
